import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getMetrics } from '@/features/metrics/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { queryKeys, useAccessToken } from '@/shared/hooks';
import { metricsApiToDto } from '../metricsAdapters';
import type { ExecutionStatusKey, MetricsDto, MetricsRange, MetricsScope } from '../metricsHelpers';
import type { ApiError } from '@/shared/data/client';

type MetricsQueryInput = {
  range: MetricsRange;
  statuses: ExecutionStatusKey[];
  tags?: string[];
  scope: MetricsScope;
  projectId?: string | null;
  entityId?: string | null;
  name?: string | null;
  day?: string | null;
  startDay?: string | null;
  endDay?: string | null;
  latestOnly?: boolean;
  refreshKey?: string | number | null;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  staleTimeMs?: number;
};

const buildStatusKey = (statuses: ExecutionStatusKey[]): string => {
  if (statuses.length === 0) return 'all';
  return [...statuses].sort().join('|');
};

const buildTagsKey = (tags: string[] | undefined): string => {
  if (!tags || tags.length === 0) return 'all';
  return [...tags].sort().join('|');
};

export const useMetricsQuery = ({
  range,
  statuses,
  tags,
  scope,
  projectId,
  entityId,
  name,
  day,
  startDay,
  endDay,
  latestOnly = false,
  refreshKey,
  enabled = true,
  refetchOnWindowFocus = true,
  staleTimeMs,
}: MetricsQueryInput): UseQueryResult<MetricsDto, ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  const statusKey = buildStatusKey(statuses);
  const tagsKey = buildTagsKey(tags);
  const nameKey = name?.trim().toLowerCase() ?? 'all';
  const entityKey = entityId ?? 'all';
  const dayKey = day ?? 'all';
  const rangeKey = startDay && endDay ? `${startDay}_${endDay}` : 'all';
  const latestKey = latestOnly ? 'latest' : 'all';
  return useQuery<MetricsDto, ApiError>({
    queryKey: queryKeys.metrics(
      orgKey,
      projectId ?? null,
      scope,
      range,
      statusKey,
      nameKey,
      entityKey,
      dayKey,
      rangeKey,
      latestKey,
      refreshKey ?? null,
      tagsKey,
    ),
    enabled: Boolean(token) && enabled,
    queryFn: async (): Promise<MetricsDto> => {
      const apiData = await getMetrics(
        {
          range,
          statuses,
          tags,
          scope,
          projectId,
          entityId,
          name,
          day,
          startDay,
          endDay,
          latestOnly,
        },
        orgForQuery,
        token,
      );
      return metricsApiToDto(apiData);
    },
    staleTime: staleTimeMs ?? 30_000,
    refetchOnWindowFocus,
  });
};
